// Certify's durable + view models (ADR-D46 decisions-adr.md:105; ADR-D61 decisions-adr.md:133;
// slice-plan.md:60-68).
//
// Certify is ADVISORY, never a blocker (D46): it runs deterministic scanners, maps an app's posture
// to a framework's derived controls, and returns a per-control pass/fail plus a would-qualify verdict.
// The two load-bearing invariants are STRUCTURAL here, not documentation:
//   - An `Assessment` is ALWAYS a Nightcrew self-assessment and NEVER an official certification
//     (D46). `resultKind` / `isOfficialCertification` are z.literal, so a mislabeled payload is
//     UNPARSEABLE - the overclaim cannot be represented.
//   - A `TrustPageView` claim is ALWAYS `{value, source, checkedAt}` (§0 #4 sourced + dated) and the
//     required disclosure is a required field - a view without a source, a date, or the disclaimer
//     fails to parse.
//
// BOUNDARY: the `statement` field is a SHAPE only. No framework's verbatim/authoritative control text
// is authored here - that is corpus + counsel territory (D61 licensing: Nightcrew's own DERIVED
// statements outward, never republished text), seeded later. No compliance figure is invented here.
import { z } from "zod";
import { NonEmptyString, Timestamp } from "./common.js";
import { RiskLevel } from "./enums.js";
// --- The framework catalog (D46; D61) ---
/**
 * The frameworks Certify assesses against (D46; slice-plan.md:67). GDPR is the always-on baseline;
 * the founder optionally selects the others. Closed set; widen only through an ADR + the corpus owner.
 */
export const FrameworkKey = z.enum(["gdpr", "soc2", "iso27001", "nis2", "hipaa"]);
/**
 * How a derived control is checked (D61: "typed deterministic, model-assisted, or manual-evidence").
 * Deterministic controls carry execution evidence; manual-evidence controls close on an owner artifact.
 */
export const CheckType = z.enum(["deterministic", "model_assisted", "manual_evidence"]);
/**
 * One derived control from the versioned corpus (D61). `statement` is a SHAPE, seeded from the corpus
 * later - never a framework's verbatim text (licensing, D61). `corpusVersion` pins which corpus
 * version produced it so a verdict traces to an exact ruleset (D61 effective dates).
 */
export const FrameworkControl = z.object({
    controlKey: NonEmptyString,
    statement: NonEmptyString,
    checkType: CheckType,
    corpusVersion: NonEmptyString,
});
// --- Evidence (dated, sourced, PII-free) ---
/** Where a finding's evidence lives: code, config, the knowledge graph, a doc, or a scanner. */
export const EvidenceKind = z.enum(["code", "config", "graph", "doc", "scan"]);
/**
 * A grounding pointer for a control or scan result (D61: "cites the exact requirement behind every
 * pass or fail"). `ref` is a `path:line`, a config location, or a graph node id - a pointer, never
 * PII. `checkedAt` dates the observation (§0 #4; app-security §7 - a verdict carries its evidence date).
 */
export const EvidencePointer = z.object({
    kind: EvidenceKind,
    ref: NonEmptyString,
    checkedAt: Timestamp,
});
// --- Control results (marked DESIGN or OPERATION, app-security §7) ---
/** A per-control outcome. `na` = the control does not apply to this app. */
export const ControlStatus = z.enum(["pass", "fail", "na"]);
/**
 * Whether a pass is claimed on DESIGN (the control is implemented) or OPERATION (it has real
 * execution evidence) - app-security §7. Effectiveness is only OPERATION where a control has run at
 * least once; anything gated on an environment that has never applied stays DESIGN.
 */
export const VerdictKind = z.enum(["design", "operation"]);
export const ControlResult = z.object({
    status: ControlStatus,
    verdictKind: VerdictKind,
    evidence: z.array(EvidencePointer),
});
// --- Deterministic scanners (slice-plan.md:63-65) ---
/** A scan run's outcome. `error` = the scanner could not run (fail closed - never a silent pass). */
export const ScanStatus = z.enum(["pass", "fail", "error"]);
/** One scanner finding. `severity` reuses the centralized RiskLevel set (enums.ts). */
export const ScanFinding = z.object({
    ruleId: NonEmptyString,
    severity: RiskLevel,
    summary: NonEmptyString,
    evidence: EvidencePointer,
});
/** Non-negative integer counts. `passed + failed <= total` (an `na`/skipped item can lift total). */
export const ScanCounts = z.object({
    passed: z.int().nonnegative(),
    failed: z.int().nonnegative(),
    total: z.int().nonnegative(),
});
/**
 * One scanner's result (slice-plan.md:65: "a pass or fail with counts"). The count invariant is
 * structural: a payload whose passed + failed exceeds total is rejected at the boundary.
 */
export const ScanResult = z
    .object({
    scannerKey: NonEmptyString,
    status: ScanStatus,
    counts: ScanCounts,
    findings: z.array(ScanFinding),
})
    .refine((v) => v.counts.passed + v.counts.failed <= v.counts.total, {
    message: "passed + failed cannot exceed total",
    path: ["counts"],
});
// --- The self-assessment (D46 - STRUCTURAL) ---
/** The overall would-qualify verdict against a framework (D46). */
export const Qualifies = z.enum(["yes", "no", "partial"]);
/** One control paired with its result - the row of a readiness report. */
export const AssessedControl = z.object({
    control: FrameworkControl,
    result: ControlResult,
});
/**
 * A framework readiness assessment (D46, decisions-adr.md:105). STRUCTURAL D46 ENFORCEMENT: an
 * Assessment is ALWAYS a Nightcrew self-assessment and NEVER an official certification. `resultKind`
 * and `isOfficialCertification` are z.literal, so an Assessment that drops the self-assessment label
 * or claims official certification is UNREPRESENTABLE and UNPARSEABLE - the overclaim cannot exist as
 * a value. `corpusVersion` ties the verdict to the exact ruleset it ran against (D61).
 */
export const Assessment = z.object({
    framework: FrameworkKey,
    qualifies: Qualifies,
    resultKind: z.literal("self_assessment"),
    isOfficialCertification: z.literal(false),
    corpusVersion: NonEmptyString,
    controls: z.array(AssessedControl),
    assessedAt: Timestamp,
});
// --- The founder-uploaded real certificate (D46) ---
/**
 * The verification lifecycle of an uploaded certificate. A closed union - no illegal state.
 * `unverified` -> `verifying` -> `verified` | `failed`.
 */
export const VerifyState = z.enum(["unverified", "verifying", "verified", "failed"]);
/**
 * A real certificate the founder obtained and uploaded (D46: "records whether the founder actually
 * holds the real certificate"). STRUCTURAL: `founderUploaded` is z.literal(true) - a Certificate is
 * ALWAYS founder-provided; Certify never mints one, so a non-founder-uploaded certificate is
 * unrepresentable. `fileRef` is a PRIVATE object-storage handle (app-security §1), never a public URL.
 * `expiresAt` must be after `issuedAt` (structural guard).
 */
export const Certificate = z
    .object({
    issuer: NonEmptyString,
    scope: NonEmptyString,
    issuedAt: Timestamp,
    expiresAt: Timestamp,
    fileRef: NonEmptyString,
    founderUploaded: z.literal(true),
    verifyState: VerifyState,
})
    .refine((v) => Date.parse(v.expiresAt) > Date.parse(v.issuedAt), {
    message: "expiresAt must be after issuedAt",
    path: ["expiresAt"],
});
// --- The Certified badge (slice-plan.md:66; mockup 04-trust.html) ---
/**
 * The badge's state. A closed set with no illegal state (a value outside it is rejected at the
 * boundary and unrepresentable at rest once the db mirrors it). `certified_current` = passing and
 * verified; `dulled` = a check later failed (mockup 04-trust.html:142 "It goes dull if the checks
 * ever fail"); `unverified` = not yet verified; `expired` = an uploaded certificate lapsed.
 */
export const BadgeState = z.enum(["certified_current", "dulled", "unverified", "expired"]);
// --- Builder health checks (slice-plan.md:63; mockups 01/02) ---
/** A health signal. `unknown` = not yet checked or the check was unavailable (fail closed). */
export const HealthStatus = z.enum(["ok", "warning", "fail", "unknown"]);
/** Email deliverability posture: the four DNS records that decide whether mail lands (mockup 02-app.html:220-223). */
export const EmailDeliverability = z.object({
    spf: HealthStatus,
    dkim: HealthStatus,
    dmarc: HealthStatus,
    mx: HealthStatus,
    checkedAt: Timestamp,
});
/** Domain + TLS certificate validity and expiry (mockup 01-marketing.html:236 "domain renews in 21 days"). */
export const DomainSsl = z.object({
    valid: z.boolean(),
    expiresAt: Timestamp.nullable(),
    checkedAt: Timestamp,
});
/** Payments provider health for the app. */
export const PaymentsHealth = z.object({
    status: HealthStatus,
    checkedAt: Timestamp,
});
/** The v1 builder health-check bundle (slice-plan.md:63): email deliverability, domain/SSL, payments. */
export const BuilderHealthCheck = z.object({
    emailDeliverability: EmailDeliverability,
    domainSsl: DomainSsl,
    payments: PaymentsHealth,
});
// --- The public trust page (slice-plan.md:66; mockup 04-trust.html) ---
/**
 * One trust-page claim, STRUCTURALLY sourced + dated (§0 #4; app-security §4). Every claim carries a
 * `value`, its `source`, and the `checkedAt` date, so a claim without a source or a date cannot be
 * shown - it fails to parse. No invented, rounded, or undated figures.
 */
export const TrustClaim = z.object({
    value: NonEmptyString,
    source: NonEmptyString,
    checkedAt: Timestamp,
});
/**
 * The programmatic readiness block on the trust page (D46). LABELED a self-assessment and NEVER
 * conflated with a real credential: `resultKind` is z.literal("self_assessment") and
 * `isOfficialCertification` is z.literal(false), so this block can never be read as an official
 * certification. Every sub-claim is `{value, source, checkedAt}`.
 *
 * The would-qualify VERDICT is itself sourced + dated (§0 #4): `source` grounds where the verdict
 * came from (e.g. `assessment:gdpr-2026.1`) and `checkedAt` dates it, so the headline claim is not
 * an undated, unsourced assertion. STRUCTURAL BACKING: a qualifying verdict (`yes` | `partial`) must
 * carry at least one sourced + dated claim - a headline "would-qualify" with zero backing is
 * UNREPRESENTABLE. A `no` verdict may carry no claims (there is nothing to substantiate).
 *
 * PROVISIONAL is STRUCTURAL, not renderer-dependent (ADR-0020 decision 2, amended Wave-3 triad
 * 2026-07-24; adr/0020-slice4-certify.md:70-75). `provisional` is REQUIRED and rides alongside the
 * verdict: it is `true` when the framework's corpus is `corpus_pending` - its licensed verbatim
 * corpus is DEFERRED to Slice 16 (e.g. SOC 2), so the would-qualify result is PRELIMINARY, assessed
 * against Nightcrew-authored control statements only. Because the field is required, the public
 * `TrustPageView` ALWAYS carries whether the readiness is provisional: the trust page cannot
 * serialize a `corpus_pending` "yes" without the flag present, so the honesty does not depend on the
 * renderer remembering (mirrors D46's structural self-assessment labeling; the correctness lens found
 * the engine COMPUTED this but the wire type dropped it). `false` = the verdict ran against the full
 * corpus (e.g. GDPR, the always-on baseline).
 */
export const ProgrammaticReadiness = z
    .object({
    resultKind: z.literal("self_assessment"),
    isOfficialCertification: z.literal(false),
    qualifies: Qualifies,
    provisional: z.boolean(),
    claims: z.array(TrustClaim),
    source: NonEmptyString,
    checkedAt: Timestamp,
})
    .refine((v) => v.qualifies === "no" || v.claims.length >= 1, {
    message: "a qualifying verdict (yes|partial) must carry at least one sourced + dated claim",
    path: ["claims"],
});
/**
 * The DISTINCT verified-credential variant (D46): a founder-uploaded, verified real certificate shown
 * on the trust page, SEPARATE from the programmatic readiness. Its `resultKind` literal is
 * "verified_credential", so it structurally cannot be conflated with `ProgrammaticReadiness`
 * (self_assessment) - the two variants are unswappable.
 *
 * Dates are COHERENCE-guarded (mirrors `Certificate` ~:186): `expiresAt` must be after `issuedAt`, so
 * a public credential cannot render with an incoherent validity window. This is a coherence check
 * ONLY - an already-PAST expiry is not a parse error; the badge state machine renders "expired"
 * (`BadgeState`) at view time, the schema just refuses dates that could never have been valid.
 */
export const VerifiedCredential = z
    .object({
    resultKind: z.literal("verified_credential"),
    issuer: NonEmptyString,
    scope: NonEmptyString,
    issuedAt: Timestamp,
    expiresAt: Timestamp,
    verifiedAt: Timestamp,
})
    .refine((v) => Date.parse(v.expiresAt) > Date.parse(v.issuedAt), {
    message: "expiresAt must be after issuedAt",
    path: ["expiresAt"],
});
/**
 * What a founder's customer sees on the public trust page (slice-plan.md:66; mockup 04-trust.html).
 * `programmaticReadiness` (the self-assessment) and `verifiedCredential` (a real, verified upload) are
 * DISTINCT fields, never merged. `disclosure` is a REQUIRED field (§0 #5; D46 disclaimer) - a view
 * without it fails to parse; its COPY is seeded later and is §6.6 expert-reviewed, never authored here.
 */
export const TrustPageView = z.object({
    badge: BadgeState,
    programmaticReadiness: ProgrammaticReadiness,
    verifiedCredential: VerifiedCredential.nullable(),
    disclosure: NonEmptyString,
    lastVerifiedAt: Timestamp,
});
//# sourceMappingURL=certify.js.map